import React, { useMemo, useState } from 'react';

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

export default function IntegrationConsentModal({ consents, error, onApprove, onDeny }) {
  const [denying, setDenying] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const current = consents?.[0] || null;

  const remainingCount = useMemo(() => Math.max((consents?.length || 0) - 1, 0), [consents]);

  if (!current) return null;

  async function handleApprove() {
    setSubmitting(true);
    try {
      await onApprove(current.id);
      setDenying(false);
      setReason('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeny() {
    setSubmitting(true);
    try {
      await onDeny(current.id, reason);
      setDenying(false);
      setReason('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay integration-consent-overlay" role="presentation">
      <div className="modal-content integration-consent-modal" role="dialog" aria-modal="true" aria-labelledby="integration-consent-title">
        <div className="modal-header">
          <div>
            <span className="integration-consent-eyebrow">Data sharing consent</span>
            <h2 id="integration-consent-title">Approve API access for {current.merchantName || 'merchant'}</h2>
          </div>
        </div>

        <div className="modal-body integration-consent-body">
          <p>
            {current.merchantName || 'This merchant'} is requesting permission to access {current.companyName || 'your company'} data through the BloomNav integration API.
          </p>
          <div className="integration-consent-summary">
            <div>
              <span>Merchant</span>
              <strong>{current.merchantName || '-'}</strong>
            </div>
            <div>
              <span>Contact</span>
              <strong>{current.merchantContactEmail || '-'}</strong>
            </div>
            <div>
              <span>Requested</span>
              <strong>{formatDate(current.requestedAt)}</strong>
            </div>
          </div>

          <div className="integration-consent-notice">
            Approving enables read access for attendance, employee, and attendance settings data. Access remains blocked until you approve.
          </div>

          {denying && (
            <label className="integration-consent-deny-reason">
              Reason for denial (optional)
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Add a note for the merchant" />
            </label>
          )}

          {error && <div className="integration-consent-error">{error}</div>}
          {remainingCount > 0 && <p className="muted">{remainingCount} more request{remainingCount === 1 ? '' : 's'} waiting after this.</p>}

          <div className="modal-footer">
            {!denying ? (
              <>
                <button type="button" className="btn btn--secondary" onClick={() => setDenying(true)} disabled={submitting}>
                  Deny
                </button>
                <button type="button" className="btn btn--primary" onClick={handleApprove} disabled={submitting}>
                  {submitting ? 'Approving...' : 'Approve & enable access'}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn--secondary" onClick={() => setDenying(false)} disabled={submitting}>
                  Back
                </button>
                <button type="button" className="btn btn--danger" onClick={handleDeny} disabled={submitting}>
                  {submitting ? 'Denying...' : 'Confirm denial'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
